import dayjs, { Dayjs } from 'dayjs';

export const DATE_PRESETS = [
  'lastWeek',
  'last30Days',
  'lastMonth',
  'lastQuarter',
  'lastYear',
] as const;

export type TDatePreset = typeof DATE_PRESETS[number];

const previousQuarter = (today: Dayjs) => {
  const quarter = Math.floor(today.month() / 3);
  const start =
    quarter === 0
      ? today.subtract(1, 'year').month(9).startOf('month')
      : today.month((quarter - 1) * 3).startOf('month');

  return {
    from: start.startOf('day'),
    till: start.add(2, 'month').endOf('month').startOf('day'),
  };
};

export const presetRange = (preset: TDatePreset, today: Dayjs = dayjs().startOf('day')) => {
  if (preset === 'lastWeek') {
    const from = today.startOf('week').subtract(1, 'week');
    return { from, till: from.endOf('week').startOf('day') };
  }

  if (preset === 'last30Days') {
    return { from: today.subtract(29, 'day'), till: today };
  }

  if (preset === 'lastMonth') {
    const from = today.subtract(1, 'month').startOf('month');
    return { from, till: from.endOf('month').startOf('day') };
  }

  if (preset === 'lastQuarter') {
    return previousQuarter(today);
  }

  const from = today.subtract(1, 'year').startOf('year');
  return { from, till: from.endOf('year').startOf('day') };
};

export const matchDatePreset = (from?: string, till?: string): TDatePreset | undefined => {
  const fromDate = from && dayjs(from).isValid() ? dayjs(from) : null;
  const tillDate = till && dayjs(till).isValid() ? dayjs(till) : null;

  if (!fromDate || !tillDate) {
    return undefined;
  }

  const today = dayjs().startOf('day');

  return DATE_PRESETS.find((preset) => {
    const range = presetRange(preset, today);
    return fromDate.isSame(range.from, 'day') && tillDate.isSame(range.till, 'day');
  });
};

const toIsoRange = (preset: TDatePreset) => {
  const range = presetRange(preset);

  return {
    from: range.from.startOf('day').toISOString(),
    till: range.till.endOf('day').toISOString(),
  };
};

export const getLastWeekDateRange = () => toIsoRange('lastWeek');

export const getLastMonthDateRange = () => toIsoRange('lastMonth');
